import React, { useEffect, useMemo, useState } from 'react';
import toast from 'react-hot-toast';
import { API_BASE, WEBSITE_URL, parseJsonResponse, getUserErrorMessage } from '../../utils/apiBase';
import {
  buildAffiliateLink,
  buildProductAffiliateLink,
  copyTextToClipboard,
  getProductSlug,
} from '../../utils/affiliateLinks';

export default function AffiliateLinkGenerator({ affiliateCode }) {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedProductId, setSelectedProductId] = useState('');

  useEffect(() => {
    const fetchProducts = async () => {
      setLoading(true);
      try {
        const response = await fetch(`${API_BASE}/v1/product?page=1&limit=200&status=active`);
        const result = await parseJsonResponse(response);
        setProducts((result.data || []).filter((product) => product.status === 'active'));
      } catch (error) {
        toast.error(getUserErrorMessage(error, 'Unable to load products.'));
        setProducts([]);
      } finally {
        setLoading(false);
      }
    };

    fetchProducts();
  }, []);

  const filteredProducts = useMemo(() => {
    const term = search.trim().toLowerCase();
    if (!term) return products;
    return products.filter((product) => {
      const name = (product.name || '').toLowerCase();
      const slug = (product.slug || '').toLowerCase();
      return name.includes(term) || slug.includes(term);
    });
  }, [products, search]);

  const selectedProduct = products.find((product) => product._id === selectedProductId);
  const storeLink = buildAffiliateLink(affiliateCode, WEBSITE_URL);
  const productLink = selectedProduct
    ? buildProductAffiliateLink(affiliateCode, getProductSlug(selectedProduct), WEBSITE_URL)
    : '';

  const handleCopyStoreLink = async () => {
    try {
      await copyTextToClipboard(storeLink);
      toast.success('Store affiliate link copied');
    } catch {
      toast.error('Unable to copy link');
    }
  };

  const handleCopyProductLink = async () => {
    if (!productLink) {
      toast.error('Please select a product first');
      return;
    }
    try {
      await copyTextToClipboard(productLink);
      toast.success('Product affiliate link copied');
    } catch {
      toast.error('Unable to copy link');
    }
  };

  return (
    <div className="affiliate-link-generator">
      <div className="affiliate-link-block">
        <div className="affiliate-link-label">Store Link</div>
        <div className="affiliate-link-preview">{storeLink}</div>
        <button type="button" className="affiliate-link-copy-btn" onClick={handleCopyStoreLink}>
          Copy Store Link
        </button>
      </div>

      <div className="affiliate-link-block">
        <div className="affiliate-link-label">Product Link</div>
        <input
          type="search"
          name="affiliateProductSearch"
          className="affiliate-form-input"
          placeholder="Search product by name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          autoComplete="off"
          data-lpignore="true"
        />
        <select
          className="affiliate-form-input"
          value={selectedProductId}
          onChange={(e) => setSelectedProductId(e.target.value)}
          disabled={loading}
        >
          <option value="">{loading ? 'Loading products...' : 'Select a product'}</option>
          {filteredProducts.map((product) => (
            <option key={product._id} value={product._id}>
              {product.name}
            </option>
          ))}
        </select>
        {productLink ? (
          <div className="affiliate-link-preview">{productLink}</div>
        ) : (
          <div className="affiliate-link-preview affiliate-link-preview--empty">
            Select a product to generate link
          </div>
        )}
        <button
          type="button"
          className="affiliate-link-copy-btn affiliate-link-copy-btn--primary"
          onClick={handleCopyProductLink}
          disabled={!productLink}
        >
          Copy Product Link
        </button>
      </div>
    </div>
  );
}
